import { useEffect, useState } from "react";
import { FileSpreadsheet, Loader2 } from "@fg/icons";
import { driveApi, type DriveItem } from "@/api/driveApi";
import { useAccessToken } from "@/auth/useAccessToken";

const SHEET_EXT_RE = /\.(xlsx?|csv|json)$/i;

/** Spreadsheets at the top of the user's Drive — used by the editor and the home screen. */
export function DriveOpenDialog({
    onClose, onPick
}: {
    onClose: () => void;
    onPick: (item: DriveItem) => void;
}) {
    const token = useAccessToken();
    const [files, setFiles] = useState<DriveItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        driveApi.listItems(null, token)
            .then((rows) => {
                if (!cancelled) setFiles(rows.filter((r) => r.kind === "File" && SHEET_EXT_RE.test(r.name)));
            })
            .catch((err: unknown) => {
                if (!cancelled) setError(err instanceof Error ? err.message : String(err));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // Load once per open — a silent token renew mustn't refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Open from Drive"
                className="w-[520px] max-w-[92vw] max-h-[80vh] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-xl flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h2 className="text-sm font-semibold">Open from Drive</h2>
                    <button type="button" onClick={onClose} aria-label="Close" className="text-sm text-muted-foreground hover:text-foreground">✕</button>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                    {loading ? (
                        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
                        </div>
                    ) : error ? (
                        <p className="px-3 py-6 text-sm text-amber-700 dark:text-amber-300">{error}</p>
                    ) : files.length === 0 ? (
                        <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                            No compatible spreadsheets in your Drive yet.
                        </p>
                    ) : (
                        <ul className="flex flex-col gap-1">
                            {files.map((f) => (
                                <li key={f.id}>
                                    <button
                                        type="button"
                                        onClick={() => onPick(f)}
                                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-accent"
                                    >
                                        <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                                        <span className="flex-1 truncate">{f.name}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {new Date(f.updatedAt).toLocaleDateString()}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}
